import React, {useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import InputWithIcon from '../../components/generics/input-with-icon';
import { Mail } from 'lucide-react';
import {observer} from "mobx-react-lite";
import {useEmailVerificationViewModel} from "../../view-model/email-verification-view-model";
import { useTranslation } from 'react-i18next';


const EmailVerification: React.FC = observer(() => {
    const viewModel = useEmailVerificationViewModel();
    const { t, i18n } = useTranslation();
    const hasVerified = useRef(false);

    const currentLang = i18n.language ;

    // Hooks
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        // Empêcher les appels multiples en mode développement (React Strict Mode)
        if (hasVerified.current) return;

        const params = new URLSearchParams(location.search);
        const token = params.get('token');

        if (token) {
            hasVerified.current = true;
            viewModel.setToken(token);
        }

    }, [location, viewModel]);

    // Composants d'état de vérification
    const renderVerifying = () => (
        <div className="text-center relative z-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">{t("emailVerification.title")}</p>
        </div>
    );

    const renderSuccess = () => (
        <div className="text-center relative z-10">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">{t("emailVerification.success")}</h1>
            <p className="text-primary mb-4">
                {t("emailVerification.adress")} <strong>{viewModel.getEmail}</strong> {t("emailVerification.suite")}.
            </p>
            <p className="text-gray-600 mb-6">{t("emailVerification.connect")}</p>
            <button 
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200" 
                onClick={() => navigate(`/${currentLang}/login`)}
            >
                {t("emailVerification.login")}
            </button>
        </div>
    );

    const renderAlreadyVerified = () => (
        <div className="text-center relative z-10">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">{t("emailVerification.alreadyVerified")}</h1>
            <p className="text-primary mb-4">
            {t("emailVerification.adress")} <strong>{viewModel.getEmail}</strong> {t("emailVerification.suite1")}.
            </p>
            <p className="text-gray-600 mb-6">{t("emailVerification.connect1")}</p>
            <button 
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200" 
                onClick={() => navigate(`/${currentLang}/login`)}
            >
                {t("emailVerification.login")}
            </button>
        </div>
    );

    const renderExpired = () => (
        <div className="text-center relative z-10">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">{t("emailVerification.expired")}</h1>
            <p className="text-gray-600 mb-6">{t("emailVerification.expired1")}</p>

            {!viewModel.getMessage ? (
                <div className="space-y-4">
                    <div className="backdrop-blur-sm bg-white/80 rounded-lg p-2">
                        <InputWithIcon
                            label="Votre adresse email"
                            type="email"
                            id="email"
                            name="email"
                            value={viewModel.getEmail}
                            onChange={(e) => viewModel.setEmail(e.target.value)}
                            placeholder="votre@email.com"
                            required
                            icon={<Mail size={18} />}
                            error={viewModel.getMessage && !viewModel.getMessage.includes("envoyé") ? viewModel.getMessage : undefined}
                        />
                    </div>
                    <button
                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
                        onClick={() => viewModel.handleResendEmailVerification()}
                    >
                        {t("emailVerification.resend")}
                    </button>
                </div>
            ) : (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 shadow-sm">
                    <p className="text-gray-700 mb-4">{viewModel.getMessage}</p>
                    {viewModel.getMessage.includes("envoyé") && (
                        <button
                            className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
                            onClick={() => navigate(`/${currentLang}/login`)}
                        >
                            {t("emailVerification.backToLogin")}
                        </button>
                    )}
                </div>
            )}
        </div>
    );

    const renderError = () => (
        <div className="text-center relative z-10">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">{t("emailVerification.error")}</h1>
            <p className="text-gray-600 mb-6">{t("emailVerification.error1")}</p>
        </div>
    );

    // Rendu principal basé sur l'état
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
            {/* Représentation subtile de calendrier/planning en arrière-plan */}
            <div className="absolute inset-0 opacity-[0.15] pointer-events-none">
                {/* Grille de planning stylisée */}
                <div className="absolute inset-0" style={{
                    backgroundImage: `
                        linear-gradient(to right, rgb(35, 168, 146) 1px, transparent 1px),
                        linear-gradient(to bottom, rgb(35, 168, 146) 1px, transparent 1px)
                    `,
                    backgroundSize: '80px 60px',
                    transform: 'perspective(800px) rotateX(45deg) scale(1.5)',
                    transformOrigin: 'center center',
                }}></div>
            </div>

            <div className="max-w-md w-full space-y-8 bg-white/95 backdrop-blur-md p-8 rounded-2xl shadow-2xl border border-gray-100 relative z-10 overflow-hidden">
                <div className="flex justify-center">
                    <Link to={`/${currentLang}/`}>
                        <img src="/prestaslot_logo.svg" alt="PrestaSlot" className="h-12 mx-auto" />
                    </Link>
                </div>
                
                <div className="mt-6">
                    {viewModel.getStatus === 'verifying' && renderVerifying()}
                    {viewModel.getStatus === 'success' && renderSuccess()}
                    {viewModel.getStatus === 'already-verified' && renderAlreadyVerified()}
                    {viewModel.getStatus === 'expired' && renderExpired()}
                    {viewModel.getStatus === 'error' && renderError()}
                </div>
            </div>
        </div>
    );
});

export default EmailVerification;